import React, { useState, useEffect, useContext } from "react";
import axios from "../../context/AxiosInstance";
import { Form, Button, Row, Col, Table, Spinner, Badge } from "react-bootstrap";
import { AuthContext } from "../../context/AuthContext";
import { toast } from "react-toastify";
import Select from "react-select";
import { FaPen, FaCalendarAlt, FaCalendarDay, FaUser, FaCoins, FaLock } from "react-icons/fa";
import { RsKpiCard, RsKpiGrid, RsEmptyHint, RsEmployeePreview } from "./SalairesUi";
import "./SalairesTabShared.css";

const ValidationSalaire = () => {
  const { token } = useContext(AuthContext);

  const currentYear = new Date().getFullYear();
  const [annee, setAnnee] = useState(currentYear);
  const [mois, setMois] = useState("");

  const [contrats, setContrats] = useState([]);
  const [crgs, setCrgs] = useState([]); // select options
  const [crgMeta, setCrgMeta] = useState([]); // raw CRG with prise_en_charge_crg, type_crg

  const [selectedContrat, setSelectedContrat] = useState(null);
  const [selectedCrg, setSelectedCrg] = useState(null);

  const [montantSimple, setMontantSimple] = useState("");
  const [tauxGain, setTauxGain] = useState("");
  const [tauxRetenu, setTauxRetenu] = useState("");
  const [montantGain, setMontantGain] = useState(0);
  const [montantRetenu, setMontantRetenu] = useState(0);

  const [loading, setLoading] = useState(false);
  const [listeSalaires, setListeSalaires] = useState([]);
  const [verrouillageInfo, setVerrouillageInfo] = useState(null);

  const anneesDisponibles = [2022, 2023, 2024, 2025, 2026];
  const moisDisponibles = [
    "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
    "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"
  ];

  // 🔁 Charger contrats et CRGs
  useEffect(() => {
    if (!token || !annee) return;

    const fetchData = async () => {
      try {
        const [resContrats, resCrgs] = await Promise.all([
          axios.get("/contrats/", {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get("/crg/", {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const contratsFiltres = resContrats.data.data.filter(c =>
          new Date(c.created_at).getFullYear() === Number(annee)
        );

        const contratOptions = contratsFiltres.map(c => ({
          value: c.id_contrat,
          label: `${c.nom_employe} ${c.prenom_employe} | ${c.poste}`,
          contrat: c,
        }));

        const crgOptions = resCrgs.data.data.map(c => ({
          value: c.id_crg,
          label: c.libelle_crg
        }));

        setContrats(contratOptions);
        setCrgs(crgOptions);
        setCrgMeta(Array.isArray(resCrgs.data.data) ? resCrgs.data.data : []);
      } catch (err) {
        toast.error("❌ Erreur chargement contrats/CRG");
      }
    };

    fetchData();
  }, [annee, token]);

  // Helpers format/parse nombres FR
  const parseNumberFr = (val) => {
    if (val === null || val === undefined) return 0;
    const s = String(val).replace(/\s/g, "").replace(/,/g, ".");
    const n = parseFloat(s);
    return isNaN(n) ? 0 : n;
  };
  const formatNumberFr = (n) => {
    try {
      return new Intl.NumberFormat("fr-FR").format(parseNumberFr(n));
    } catch {
      return String(n ?? "");
    }
  };

  // 🔁 Recalculs automatiques
  useEffect(() => {
    const ms = parseNumberFr(montantSimple);
    const tg = parseNumberFr(tauxGain);
    const tr = parseNumberFr(tauxRetenu);
    const gain = ms * (tg / 100);
    const retenu = ms * (tr / 100);
    setMontantGain(gain);
    setMontantRetenu(retenu);
  }, [montantSimple, tauxGain, tauxRetenu]);

  // 🔁 Rechargement des salaires déjà saisis pour ce contrat + mois + annee
  useEffect(() => {
    if (selectedContrat && mois && annee) {
      fetchSalairesDuContrat();
    } else {
      // Réinitialiser les informations de verrouillage si les sélections changent
      setVerrouillageInfo(null);
      setListeSalaires([]);
    }
  }, [selectedContrat, mois, annee]);

  const fetchSalairesDuContrat = async () => {
    try {
      const res = await axios.get("/salaires/details", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          id_contrat: selectedContrat.value,
          annee,
          mois,
        },
      });
      setListeSalaires(res.data.data || []);
      
      // 🔒 Vérifier le statut de verrouillage
      try {
        const verrouillageRes = await axios.get("/salaires/verifier-verrouillage/", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            id_contrat: selectedContrat.value,
            annee,
            mois,
          },
        });
        setVerrouillageInfo(verrouillageRes.data.data);
      } catch (verrouillageErr) {
        console.log("Erreur vérification verrouillage:", verrouillageErr);
        setVerrouillageInfo(null);
      }
    } catch (err) {
      toast.error("❌ Erreur chargement salaires");
    }
  };

  const handleSave = async () => {
    if (!selectedContrat || !selectedCrg || !mois || !annee) {
      toast.warning("⚠️ Veuillez remplir tous les champs requis");
      return;
    }

    try {
      setLoading(true);
      
      // 🔒 Vérifier d'abord si les salaires sont verrouillés pour cette période
      try {
        const verrouillageRes = await axios.get("/salaires/verifier-verrouillage/", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            id_contrat: Number(selectedContrat.value),
            annee: Number(annee),
            mois: mois,
          },
        });
        
        if (verrouillageRes.data.data.is_verrouille) {
          toast.error("🔒 Impossible d'ajouter un salaire : les salaires de " + mois + " " + annee + " sont verrouillés pour cet employé.");
          return;
        }
      } catch (verrouillageErr) {
        console.log("Erreur vérification verrouillage:", verrouillageErr);
        // Continuer même si la vérification échoue
      }

      const payload = {
        id_contrat: Number(selectedContrat.value),
        id_crg: Number(selectedCrg.value),
        mois_salaire: mois,
        annee_salaire: Number(annee),
        montant_simple: parseNumberFr(montantSimple),
        taux_gain_salaire: parseNumberFr(tauxGain),
        taux_retenu_salaire: parseNumberFr(tauxRetenu),
        montant_gain_salaire: parseNumberFr(montantGain),
        montant_retenu_salaire: parseNumberFr(montantRetenu),
      };

      await axios.post("/salaires/", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success("✅ Salaire enregistré avec succès");
      // reset montant
      setMontantSimple("");
      setTauxGain("");
      setTauxRetenu("");
      setMontantGain(0);
      setMontantRetenu(0);
      fetchSalairesDuContrat();
    } catch (err) {
      if (err.response && err.response.status === 400) {
        // Vérifier si c'est une erreur de verrouillage
        if (err.response.data?.detail && err.response.data.detail.includes("verrouillés")) {
          toast.error("🔒 " + err.response.data.detail);
        } else {
          toast.error(err.response.data?.message || "❌ Salaire déjà enregistré !");
        }
      } else {
        toast.error("❌ Erreur lors de l'enregistrement");
      }
    } finally {
      setLoading(false);
    }
  };

  // Calculer les totaux (mêmes KPI que RechercheSalaire)
  const crgById = React.useMemo(() => {
    const map = new Map();
    (crgMeta || []).forEach(c => { map.set(c.id_crg, c); });
    return map;
  }, [crgMeta]);

  const totalBrut = listeSalaires.reduce((sum, s) => sum + (s.montant_gain_salaire || 0), 0);
  const totalChargeSalariale = listeSalaires.reduce((sum, s) => {
    const meta = crgById.get(s.id_crg);
    const isSalariale = (meta?.prise_en_charge_crg || '').toLowerCase() === 'salariale';
    return sum + (isSalariale ? (s.montant_retenu_salaire || 0) : 0);
  }, 0);
  const totalChargePatronale = listeSalaires.reduce((sum, s) => {
    const meta = crgById.get(s.id_crg);
    const isPatronale = (meta?.prise_en_charge_crg || '').toLowerCase() === 'patronale';
    const typeVal = (meta?.type_crg || '').toLowerCase();
    const isCotisation = typeVal.includes('cotisation'); // gère "3_Cotisation"
    return sum + (isPatronale && isCotisation ? (s.montant_retenu_salaire || 0) : 0);
  }, 0);
  const totalNet = totalBrut - totalChargeSalariale;
  const isReady = Boolean(selectedContrat && mois && annee);
  const contratInfo = selectedContrat?.contrat;

  return (
    <div className="salaires-tab-unified salaires-saisie">
      <div className="salaires-toolbar">
        <div className="salaires-toolbar-header">
          <h4 className="salaires-toolbar-title">
            <FaPen /> Saisie des salaires
          </h4>
        </div>

        {verrouillageInfo?.is_verrouille && (
          <div className="rs-lock-alert mb-3">
            <FaLock />
            <div>
              <strong>Salaires verrouillés</strong>
              <small>
                Les salaires de {mois} {annee} sont verrouillés pour cet employé. Aucun ajout ni modification possible — impression des bulletins uniquement.
              </small>
            </div>
          </div>
        )}

        <Form>
          <Row className="g-3 salaires-filters-row">
            <Col md={3}>
              <Form.Label><FaCalendarAlt /> Année</Form.Label>
              <Form.Select value={annee} onChange={(e) => setAnnee(Number(e.target.value))}>
                <option value="">— Sélectionner —</option>
                {anneesDisponibles.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3}>
              <Form.Label><FaCalendarDay /> Mois</Form.Label>
              <Form.Select value={mois} onChange={(e) => setMois(e.target.value)}>
                <option value="">— Sélectionner —</option>
                {moisDisponibles.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label><FaUser /> Employé</Form.Label>
              <div className="rs-select-wrap">
                <Select
                  options={contrats}
                  value={selectedContrat}
                  onChange={setSelectedContrat}
                  placeholder="Rechercher un employé…"
                  isSearchable
                  isClearable
                  classNamePrefix="rs"
                />
              </div>
            </Col>
          </Row>

          <Row className="g-3 salaires-filters-row mt-2">
            <Col md={3}>
              <Form.Label><FaCoins /> CRG</Form.Label>
              <div className="rs-select-wrap">
                <Select
                  options={crgs}
                  value={selectedCrg}
                  onChange={setSelectedCrg}
                  placeholder="Sélectionner un CRG…"
                  isSearchable
                  isClearable
                  classNamePrefix="rs"
                />
              </div>
            </Col>
            <Col md={2}>
              <Form.Label>Montant simple</Form.Label>
              <Form.Control
                type="text"
                value={montantSimple}
                onChange={(e) => {
                  const raw = e.target.value.replace(/\s/g, "");
                  if (/^[0-9,.]*$/.test(raw)) {
                    setMontantSimple(raw);
                  }
                }}
                inputMode="decimal"
              />
            </Col>
            <Col md={2}>
              <Form.Label>Taux gain (%)</Form.Label>
              <Form.Control
                type="text"
                value={tauxGain}
                onChange={(e) => {
                  const v = e.target.value.replace(/\s/g, "");
                  if (/^[0-9,\.]*$/.test(v)) setTauxGain(v);
                }}
                inputMode="decimal"
              />
            </Col>
            <Col md={2}>
              <Form.Label>Taux retenue (%)</Form.Label>
              <Form.Control
                type="text"
                value={tauxRetenu}
                onChange={(e) => {
                  const v = e.target.value.replace(/\s/g, "");
                  if (/^[0-9,\.]*$/.test(v)) setTauxRetenu(v);
                }}
                inputMode="decimal"
              />
            </Col>
            <Col md={3} className="rs-btn-col">
              <Form.Label className="rs-btn-label-hidden">Action</Form.Label>
              <Button
                className="btn-rs-save"
                onClick={handleSave}
                disabled={loading || verrouillageInfo?.is_verrouille}
                title={verrouillageInfo?.is_verrouille ? "Salaires verrouillés - Ajout impossible" : "Enregistrer le salaire"}
              >
                {loading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Enregistrement…
                  </>
                ) : verrouillageInfo?.is_verrouille ? (
                  <>
                    <FaLock className="me-1" /> Verrouillé
                  </>
                ) : (
                  "Enregistrer le salaire"
                )}
              </Button>
            </Col>
          </Row>

          <Row className="g-3 salaires-filters-row mt-2">
            <Col md={3}>
              <Form.Label>Montant gain</Form.Label>
              <Form.Control
                type="text"
                value={`${montantGain.toLocaleString()} GNF`}
                className="rs-readonly-field rs-readonly-gain"
                readOnly
              />
            </Col>
            <Col md={3}>
              <Form.Label>Montant retenue</Form.Label>
              <Form.Control
                type="text"
                value={`${montantRetenu.toLocaleString()} GNF`}
                className="rs-readonly-field rs-readonly-retenue"
                readOnly
              />
            </Col>
          </Row>
        </Form>
      </div>

      {!isReady && (
        <RsEmptyHint title="Comment commencer">
          Sélectionnez l&apos;année, le mois et l&apos;employé, puis renseignez les lignes de salaire (CRG, montants) pour enregistrer.
        </RsEmptyHint>
      )}

      {isReady && contratInfo && (
        <RsEmployeePreview
          nom={contratInfo.nom_employe}
          prenom={contratInfo.prenom_employe}
          poste={contratInfo.poste}
          meta={
            <>
              <span className="rs-meta-badge">{mois} {annee}</span>
              {verrouillageInfo?.is_verrouille && (
                <span className="rs-meta-badge rs-meta-locked">
                  <FaLock className="me-1" /> Verrouillé
                </span>
              )}
            </>
          }
        />
      )}

      {isReady && listeSalaires.length > 0 && (
        <RsKpiGrid>
          <RsKpiCard label="Salaire brut" value={totalBrut} variant="brut" />
          <RsKpiCard label="Salaire net" value={totalNet} variant="net" />
          <RsKpiCard label="Charge salariale" value={totalChargeSalariale} variant="charge-sal" />
          <RsKpiCard label="Charge patronale" value={totalChargePatronale} variant="charge-pat" />
        </RsKpiGrid>
      )}

      {isReady && (
      <div className="salaires-table-card">
        <div className="table-section-header d-flex justify-content-between align-items-center">
          <h6>Salaires enregistrés</h6>
          <Badge className="rs-count-badge">{listeSalaires.length} élément(s)</Badge>
        </div>
        {listeSalaires.length === 0 ? (
          <div className="salaires-empty-hint m-3">
            <strong>Aucun salaire enregistré</strong>
            Sélectionnez une période et un employé pour commencer la saisie.
          </div>
        ) : (
          <div className="table-responsive">
            <Table className="mb-0 rs-table-modern">
              <thead>
                <tr>
                  <th>CRG</th>
                  <th>Montant simple</th>
                  <th>Taux gain</th>
                  <th>Taux retenue</th>
                  <th>Montant gain</th>
                  <th>Montant retenue</th>
                </tr>
              </thead>
              <tbody>
                {listeSalaires.map((s, idx) => (
                  <tr key={idx}>
                    <td className="col-crg">{crgs.find((c) => c.value === s.id_crg)?.label || s.id_crg}</td>
                    <td>{s.montant_simple.toLocaleString()} GNF</td>
                    <td><Badge bg="primary">{s.taux_gain_salaire}%</Badge></td>
                    <td><Badge bg="danger">{s.taux_retenu_salaire}%</Badge></td>
                    <td className="col-gain">{s.montant_gain_salaire.toLocaleString()} GNF</td>
                    <td className="col-retenue">{s.montant_retenu_salaire.toLocaleString()} GNF</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default ValidationSalaire;
